import { Injectable } from '@angular/core';
import { Router, RouteConfigLoadStart, RouteConfigLoadEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AdminRoutesEnum } from '../enums/routes.enum';
import {
  getUserMenuListRoutes,
  ADMIN_MENU_LIST_ROUTES,
} from '../components/menu-list/menu-list-routes';

const SPINNER_MIN_DISPLAY_TIME = 300;

@Injectable({
  providedIn: 'root',
})
export class RouteLoadingTrackerService {
  private loadingStates: Set<string> = new Set();
  private userRouteToMenuItemMap: Record<string, string> = {};
  private adminRouteToMenuItemMap: Record<string, string> = {};

  constructor(private router: Router) {
    this.buildRouteMappings();
    this.initializeRouteTracking();
  }

  isMenuItemLoading(menuItemName: string): boolean {
    return this.loadingStates.has(menuItemName);
  }

  private buildRouteMappings(): void {
    // Build route mappings from menu configuration
    this.userRouteToMenuItemMap = this.extractRouteMappings(
      getUserMenuListRoutes({
        crewManagementType: 1,
        departmentType: 1,
      })
    );
    this.adminRouteToMenuItemMap = this.extractRouteMappings(ADMIN_MENU_LIST_ROUTES);
  }

  private extractRouteMappings(menuSections: any[]): Record<string, string> {
    const mapping: Record<string, string> = {};
    menuSections.forEach((section) => {
      // Check if section itself has a route (for lazy modules)
      if (section.route && section.name) {
        const routeKey = this.normalizeRouteKey(section.route);
        mapping[routeKey] = section.name;
      }

      // Also check nested items
      if (section.children) {
        section.children.forEach((menuItem: any) => {
          if (menuItem.route && menuItem.name) {
            const routeKey = this.normalizeRouteKey(menuItem.route);
            mapping[routeKey] = menuItem.name;
          }
        });
      }
    });

    return mapping;
  }

  private normalizeRouteKey(route: string): string {
    // Remove leading slash and admin prefix, then extract base route
    return route
      .replace(/^\//, '')
      .replace(/^admin\//, '')
      .split('/')[0];
  }

  private initializeRouteTracking(): void {
    this.router.events
      .pipe(filter((event) => event instanceof RouteConfigLoadStart))
      .subscribe((event: RouteConfigLoadStart) => {
        this.handleRouteEvent(event.route.path || '', true);
      });

    this.router.events
      .pipe(filter((event) => event instanceof RouteConfigLoadEnd))
      .subscribe((event: RouteConfigLoadEnd) => {
        this.handleRouteEvent(event.route.path || '', false);
      });
  }

  private handleRouteEvent(fullPath: string, isLoading: boolean): void {
    const { route, isAdmin } = this.extractRouteInfo(fullPath);
    const routeMap = isAdmin ? this.adminRouteToMenuItemMap : this.userRouteToMenuItemMap;
    const menuItemName = routeMap[route];

    if (menuItemName) {
      if (isLoading) {
        this.loadingStates.clear();
        this.loadingStates.add(menuItemName);
      } else {
        setTimeout(() => {
          this.loadingStates.delete(menuItemName);
        }, SPINNER_MIN_DISPLAY_TIME);
      }
    }
  }

  private static readonly adminSubRoutes: string[] = Object.values(AdminRoutesEnum).filter(
    (route) => route !== AdminRoutesEnum.Admin
  );

  private extractRouteInfo(path: string): { route: string; isAdmin: boolean } {
    const segments = path.split('/');
    const firstSegment = segments[0];

    // Handle admin routes
    if (firstSegment === AdminRoutesEnum.Admin) {
      return {
        route: segments.length === 1 ? AdminRoutesEnum.Admin : segments[1],
        isAdmin: segments.length > 1, // Admin root uses userRouteMap, sub-routes use adminRouteMap
      };
    }

    // For all other routes (user routes or admin sub-routes without "admin/" prefix)
    return {
      route: firstSegment || path,
      isAdmin: RouteLoadingTrackerService.adminSubRoutes.includes(firstSegment),
    };
  }
}
